import { useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../client';

// Decode JWT to get expiration time
function getTokenExpiration(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
}

// Check if token will expire within the threshold (default 5 minutes)
function isTokenExpiringSoon(token: string, thresholdMs: number = 5 * 60 * 1000): boolean {
  const expiration = getTokenExpiration(token);
  if (!expiration) return true;
  return Date.now() + thresholdMs >= expiration;
}

/**
 * Hook that automatically refreshes the auth token when:
 * 1. User is active (mouse/keyboard/touch/scroll activity)
 * 2. Token is about to expire (within 5 minutes of expiry)
 * 
 * This ensures users don't get logged out while actively using the app.
 */
export function useTokenRefresh() {
  const lastActivityRef = useRef<number>(Date.now());
  const refreshingRef = useRef<boolean>(false);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Activity threshold: consider user active if activity within last 2 minutes
  const ACTIVITY_THRESHOLD_MS = 2 * 60 * 1000;
  // Token refresh threshold: refresh if token expires within 5 minutes
  const TOKEN_EXPIRY_THRESHOLD_MS = 5 * 60 * 1000;
  // Check interval: check every 30 seconds
  const CHECK_INTERVAL_MS = 30 * 1000;

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const isUserActive = useCallback((): boolean => {
    return Date.now() - lastActivityRef.current < ACTIVITY_THRESHOLD_MS;
  }, [ACTIVITY_THRESHOLD_MS]);

  const refreshTokenIfNeeded = useCallback(async () => {
    // Prevent concurrent refresh attempts
    if (refreshingRef.current) return;

    const accessToken = localStorage.getItem('accessToken');

    // No access token — user not logged in
    if (!accessToken) return;

    // Check if user is active
    if (!isUserActive()) {
      return; // User inactive — skip refresh
    }

    // Check if token is expiring soon
    if (!isTokenExpiringSoon(accessToken, TOKEN_EXPIRY_THRESHOLD_MS)) {
      return; // Token still has plenty of time
    }

    refreshingRef.current = true;

    try {
      // Use raw fetch to avoid ApiClient's 401 retry loop.
      // No body needed — the httpOnly refreshToken cookie is sent automatically.
      const res = await fetch(`${apiClient['baseUrl']}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!res.ok) return;

      const json = await res.json();
      const data = json.data ?? json;

      if (!data.accessToken) return;

      apiClient.setAccessToken(data.accessToken);
      localStorage.setItem('accessToken', data.accessToken);
    } catch {
      // Don't clear tokens here — let the 401 handler deal with expired tokens
    } finally {
      refreshingRef.current = false;
    }
  }, [isUserActive, TOKEN_EXPIRY_THRESHOLD_MS]);

  useEffect(() => {
    // Activity event listeners
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    
    // Throttle activity updates to avoid too many updates
    let activityTimeout: ReturnType<typeof setTimeout> | null = null;
    const throttledUpdateActivity = () => {
      if (!activityTimeout) {
        updateActivity();
        activityTimeout = setTimeout(() => {
          activityTimeout = null;
        }, 1000); // Throttle to once per second
      }
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, throttledUpdateActivity, { passive: true });
    });

    // Start periodic token check
    checkIntervalRef.current = setInterval(refreshTokenIfNeeded, CHECK_INTERVAL_MS);

    // Initial check
    refreshTokenIfNeeded();

    return () => {
      // Cleanup
      activityEvents.forEach(event => {
        window.removeEventListener(event, throttledUpdateActivity);
      });
      
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
    };
  }, [updateActivity, refreshTokenIfNeeded, CHECK_INTERVAL_MS]);
}
